import { useEffect, useMemo, useState, type Dispatch, type SetStateAction } from 'react';

import type { ADNode } from '@atlaskit/editor-common/utils/validator';
import type { ADFEntity } from '@atlaskit/adf-utils/types';
import { traverse } from '@atlaskit/adf-utils/traverse/main';

import { FORGE_DOC_UPDATED_EVENT } from '@atlassian/xen-editor-provider/utils/constants';

import { expVal } from '@confluence/feature-experiments';
import { getLogger } from '@confluence/logger';

type AdfType = ADNode | string | null;

const logger = getLogger('table-of-contents:useCurrentAdf');

/**
 * Parse a stringified ADF into an object form, returning the same reference for
 * the same input string identity so downstream consumers can rely on object identity for memoization.
 */
const useStableParsedAdf = (adf: AdfType): AdfType =>
	useMemo(() => {
		if (typeof adf !== 'string') return adf;
		try {
			return JSON.parse(adf);
		} catch (err) {
			logger.error`Failed to parse ADF string in useCurrentAdf: ${err}`;
			return adf;
		}
	}, [adf]);

/**
 * Cheap structural check: returns true if the ADF contains any extension-style
 * node (`extension`, `bodiedExtension`, `inlineExtension`)
 */
const hasAnyExtensionNode = (adf: AdfType): boolean => {
	if (!adf) return false;
	let adfObject = adf as ADFEntity;
	if (typeof adf === 'string') {
		try {
			adfObject = JSON.parse(adf);
		} catch {
			return false;
		}
	}
	let found = false;
	const markFound = (node: ADFEntity) => {
		found = true;
		return node;
	};
	traverse((adfObject || {}) as ADFEntity, {
		extension: markFound,
		bodiedExtension: markFound,
		inlineExtension: markFound,
	});
	return found;
};

/**
 * Hook for Table of Contents macro that updates headings and links when the ADF
 * is modified on the frontend by a Forge app
 */
export const useCurrentAdf = (adf: AdfType, isInEditMode: boolean) => {
	// Parse string ADF once and propagate a stable object reference downstream
	const parsedAdf = useStableParsedAdf(adf);
	const [currentAdf, setCurrentAdf] = useState<AdfType>(parsedAdf);

	// When the perf experiment is on, only subscribe to the Forge doc-updated event
	// when the page contains at least one extension node (extension / bodiedExtension / inlineExtension).
	const shouldListenForForgeUpdates = useMemo(() => {
		if (!expVal('confluence_toc_render_perf', 'isEnabled', false)) return true;
		return hasAnyExtensionNode(parsedAdf);
	}, [parsedAdf]);

	useEffect(() => {
		if (!isInEditMode && shouldListenForForgeUpdates) {
			const onForgeDocUpdated = (event: Event) => handleDocUpdated(event, setCurrentAdf);
			document.addEventListener(FORGE_DOC_UPDATED_EVENT, onForgeDocUpdated);
			return () => document.removeEventListener(FORGE_DOC_UPDATED_EVENT, onForgeDocUpdated);
		}
	}, [isInEditMode, shouldListenForForgeUpdates]);

	// Use parsed adf in edit mode to keep TOC updated with user changes (still
	// using the stable parsed reference so identity memos remain effective).
	return isInEditMode ? parsedAdf : currentAdf;
};

// Exported for testing only
export const handleDocUpdated = (event: Event, setAdf: Dispatch<SetStateAction<AdfType>>) => {
	const { forgeDoc, localId } = (event as CustomEvent).detail;

	setAdf((prevAdf) => {
		let adfObject = prevAdf as ADFEntity;
		if (typeof prevAdf === 'string') {
			try {
				adfObject = JSON.parse(prevAdf);
			} catch (_err) {
				// Swallow the error as ADF format error handling is expected to be done earlier,
				// in the component that renders the entire content
				return prevAdf;
			}
		}

		const newAdf = traverse(adfObject, {
			// Match the triggering macro's localId in the ADF and update the macro content
			bodiedExtension: (node) => {
				if (node.attrs?.localId === localId) {
					const newContent = forgeDoc?.children?.[0]?.props?.document?.content;
					if (newContent) {
						node.content = newContent;
					}
					return node;
				}
			},
		});

		if (newAdf) {
			return newAdf as ADNode;
		}
		return prevAdf;
	});
};
